import { useState } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { BookOpen, Heart, Shield, Sparkles, Menu, X } from "lucide-react";
import LanguageSwitcher from "@/components/LanguageSwitcher";

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: "easeOut" }
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.15
    }
  }
};

export default function LandingPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { t, i18n } = useTranslation();

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
  };

  const features = [
    {
      icon: BookOpen,
      title: t('landing.features.dailyReflection'),
      description: t('landing.features.dailyReflectionDesc'),
      color: "bg-emerald-500"
    },
    {
      icon: Sparkles,
      title: t('landing.features.insight'),
      description: t('landing.features.insightDesc'),
      color: "bg-orange-500"
    },
    {
      icon: Heart,
      title: t('landing.features.emotionalAwareness'),
      description: t('landing.features.emotionalAwarenessDesc'),
      color: "bg-blue-500"
    },
    {
      icon: Shield,
      title: t('landing.features.safePrivate'),
      description: t('landing.features.safePrivateDesc'),
      color: "bg-amber-500"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-100 via-yellow-50 via-40% to-emerald-100">
      {/* Navigation */}
      <motion.nav 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="fixed top-0 left-0 right-0 z-50 bg-amber-50/80 backdrop-blur-md border-b border-border/50"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Left side - Language switcher only */}
            <div className="flex items-center">
              <LanguageSwitcher currentLang={i18n.language} onChangeLang={changeLanguage} />
            </div>

            {/* Right side - Logo as menu button */}
            <button 
              className="p-1 border-2 border-black rounded-md hover:bg-black/5 transition-colors" 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="menu-logo-button"
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_mindairy-site/artifacts/6hwqvtsj_mindary%20logo.jpeg" 
                alt="mindary logo" 
                className="w-10 h-10 object-contain"
              />
            </button>
          </div>
        </div>

        {/* Dropdown menu from logo button */}
        {mobileMenuOpen && (
          <motion.div 
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            className="absolute right-4 top-16 w-48 bg-white/95 backdrop-blur-md border border-border rounded-lg shadow-lg z-50"
          >
            <div className="py-2">
              <Link to="/" className="block px-4 py-2 text-muted-foreground hover:text-foreground hover:bg-gray-100" onClick={() => setMobileMenuOpen(false)}>
                Hjem
              </Link>
              <Link to="/adsi-develop" className="block px-4 py-2 text-muted-foreground hover:text-foreground hover:bg-gray-100" onClick={() => setMobileMenuOpen(false)}>
                {t('nav.adsiDevelop')}
              </Link>
              <Link to="/personvern" className="block px-4 py-2 text-muted-foreground hover:text-foreground hover:bg-gray-100" onClick={() => setMobileMenuOpen(false)}>
                {t('nav.privacy')}
              </Link>
              <Link to="/vilkaar-og-ansvar" className="block px-4 py-2 text-muted-foreground hover:text-foreground hover:bg-gray-100" onClick={() => setMobileMenuOpen(false)}>
                {t('nav.terms')}
              </Link>
              <Link to="/kontakt" className="block px-4 py-2 text-muted-foreground hover:text-foreground hover:bg-gray-100" onClick={() => setMobileMenuOpen(false)}>
                {t('nav.contact')}
              </Link>
              <Link to="/admin" className="block px-4 py-2 text-muted-foreground hover:text-foreground hover:bg-gray-100" onClick={() => setMobileMenuOpen(false)}>
                {t('nav.admin')}
              </Link>
            </div>
          </motion.div>
        )}
      </motion.nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-8 lg:pt-40 lg:pb-12 overflow-hidden">

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="max-w-3xl mx-auto text-center"
          >
            <motion.h1 
              variants={fadeInUp}
              className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold text-foreground tracking-tight leading-tight mb-6"
              data-testid="hero-title"
            >
              {t('landing.heroTitle')}<br />
              <span className="bg-gradient-to-r from-emerald-500 via-blue-500 to-orange-500 bg-clip-text text-transparent italic">{t('landing.heroTitleHighlight')}</span>
            </motion.h1>
            <motion.p 
              variants={fadeInUp}
              className="text-base md:text-lg text-muted-foreground mb-8 max-w-xl mx-auto leading-relaxed"
            >
              {t('landing.heroDescription')}
            </motion.p>
            <motion.div variants={fadeInUp} className="flex justify-center">
              <Link to="/last-ned">
                <Button 
                  size="lg" 
                  className="rounded-full px-8 hover:scale-105 transition-transform bg-emerald-500 hover:bg-emerald-600"
                  data-testid="cta-button"
                >
                  {t('landing.getStarted')}
                </Button>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="pt-2 pb-8 lg:pt-4 lg:pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-10"
          >
            <h2 className="font-display text-3xl sm:text-4xl font-semibold text-foreground tracking-tight" data-testid="features-title">
              {t('landing.whyMindary')}
            </h2>
          </motion.div>

          <motion.div 
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8"
          >
            {features.map((feature, index) => (
              <motion.div key={index} variants={fadeInUp}>
                <Card className="h-full bg-card/80 backdrop-blur-sm border-border/50 hover:shadow-lg transition-all duration-300 feature-card" data-testid={`feature-card-${index}`}>
                  <CardContent className="p-8">
                    <div className={`w-12 h-12 rounded-xl ${feature.color} flex items-center justify-center mb-6`}>
                      <feature.icon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="font-display text-xl font-semibold text-foreground mb-3">
                      {feature.title}
                    </h3>
                    <p className="text-muted-foreground leading-relaxed">
                      {feature.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-border/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-4">
            <div className="flex items-center justify-center gap-6">
              <Link to="/personvern" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                {t('nav.privacy')}
              </Link>
              <Link to="/vilkaar-og-ansvar" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                {t('nav.terms')}
              </Link>
            </div>
            <div className="text-center">
              <p className="text-sm text-muted-foreground">
                {t('footer.copyright')}
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
